// src/pages/admin/RoleMapping.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Stack,
  Alert,
  Snackbar,
  useMediaQuery,
  useTheme,
  Drawer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { useSelector } from 'react-redux';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx'; 

import {
  useListRoleMappingsQuery,
  useCreateRoleMappingMutation,
  useUpdateRoleMappingMutation,
  useDeleteRoleMappingMutation,
  RoleMapping,
  Permission,
  MENU_ITEMS,
} from '../../features/admin/roleMapping.ts';
import { useListRolesQuery } from '../../features/admin/roleMasterApi.ts';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

// Menu structure for sidebar - same as in adminSlidemenu.tsx
const SIDE_MENU_ITEMS = [
  { id: 'dashboard', name: 'Dashboard', path: '/adminDashboard' },
  { id: 'user_list', name: 'User List', path: '/Userlist' },
  { id: 'product_management', name: 'Product Management', path: '/productManagement' },
  { id: 'category_management', name: 'Category Management', path: '/categoryManagement' },
  { id: 'unit_management', name: 'Unit Management', path: '/unitManagement' },
  { id: 'order_management', name: 'Order Management', path: '/adminOrderManagement' },
  { id: 'warranty_management', name: 'Warranty Management', path: '/WarrantyManagement' },
  { id: 'subcategory_management', name: 'Sub Category Management', path: '/subcategorymanagement' },
  { id: 'delivery_agent_management', name: 'Delivery Agent Management', path: '/DeliveryAgentManagement' },
  { id: 'warehouse_management', name: 'Warehouse Management', path: '/WarehouseManagement' },
  { id: 'banner_management', name: 'Banner Management', path: '/banner' },
  { id: 'subbanner_management', name: 'Sub Banner Management', path: '/subbanner' },
  { id: 'review', name: 'Review', path: '/review' },
  { id: 'guest_order_list', name: 'Guest User Details', path: '/guestOrderlist' },
  { id: 'guest_user_order', name: 'Guest Users Order List', path: '/guestUserOrder' },
  { id: 'set_credit', name: 'Set Credit for User', path: '/SetCredit' },
  { id: 'product_warehouse_management', name: 'Product Warehouse Management', path: '/ProductWarehouseManagement' },
  { id: 'deal_of_the_day', name: 'Deal of the Day', path: '/dealoftheday' },
  { id: 'coupon_management', name: 'Coupon Management', path: '/couponManagement' },
  { id: 'admin_coupon_users', name: 'Coupon Admin Management', path: '/AdminCouponUsers' },
  { id: 'reward_management', name: 'Reward Management', path: '/rewardManagement' },
  { id: 'reward_settings_management', name: 'Reward Settings Management', path: '/RewardSettingsManagement' },
  { id: 'admin_rewards', name: 'Admin Reward Management', path: '/AdminRewards' },
  { id: 'analytics', name: 'Report & Analytics', path: '/analytics' },
  { id: 'flash_sale', name: 'Flash Sale', path: '/flashSale' },
  { id: 'delivery_management', name: 'Delivery Management', path: '/deliveryManagement' },
  { id: 'shipping_management', name: 'Shipping Management', path: '/shippingManagement' },
  { id: 'delivery_settings', name: 'Delivery Settings', path: '/deliverySettings' },
  { id: 'admin_users', name: 'Admin Users', path: '/adminUsers' },
  { id: 'settings', name: 'Settings', path: '/settings' },
  { id: 'role_master', name: 'Role Master', path: '/roleMaster' },
  { id: 'role_mapping', name: 'Role Mapping', path: '/roleMapping' },
  { id: 'register_admin', name: 'Manage Admin User', path: '/RegisterAdmin' },
];

const RoleMappingManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState<RoleMapping | null>(null);
  const [formData, setFormData] = useState({
    role_id: '',
    admin_type: '' as 'super_admin' | 'admin' | 'operation' | '',
    module_or_screen: '',
    rights: '',
    permissions: [] as Permission[],
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Get current admin info from redux or localStorage
  const currentAdminRole = useSelector((state: any) => state.adminAuth?.adminType) || 
                          localStorage.getItem('admin_type') || 
                          localStorage.getItem('user_role') || 'operation';
  
  // RTK Query hooks
  const { 
    data: mappingsData, 
    isLoading: isLoadingMappings, 
    refetch,
    error: mappingsError 
  } = useListRoleMappingsQuery();
  
  const { data: rolesData, isLoading: isLoadingRoles } = useListRolesQuery();
  const [createMapping, { isLoading: isCreating }] = useCreateRoleMappingMutation();
  const [updateMapping, { isLoading: isUpdating }] = useUpdateRoleMappingMutation();
  const [deleteMapping, { isLoading: isDeleting }] = useDeleteRoleMappingMutation();

  // Debug: Log API responses
  useEffect(() => {
    console.log('Mappings Data:', mappingsData);
    console.log('Mappings Error:', mappingsError);
    console.log('Roles Data:', rolesData);
    console.log('Current Admin Role:', currentAdminRole);
  }, [mappingsData, mappingsError, rolesData, currentAdminRole]);

  // Check if current admin has permission to access this page - Only super_admin can access
  const hasPermission = () => {
    if (!currentAdminRole) return false;
    
    const role = currentAdminRole.toString().toLowerCase().trim();
    
    // Only super admin can access role mapping page
    return role === 'super_admin' || 
           role === 'super admin' ||
           role === 'superadmin' ||
           role.includes('super');
  };

  // Get permissions for current admin role
  const getCurrentAdminPermissions = () => {
    if (!mappingsData?.role_mappings) return [];
    
    const currentMapping = mappingsData.role_mappings.find(mapping => 
      mapping.role_name?.toLowerCase() === currentAdminRole.toLowerCase() ||
      mapping.admin_type?.toLowerCase() === currentAdminRole.toLowerCase()
    );
    
    if (!currentMapping) return [];
    
    // Parse permissions from current mapping
    const rights = currentMapping.rights ? currentMapping.rights.split(',').map(r => r.trim()) : [];
    const modules = currentMapping.module_or_screen ? currentMapping.module_or_screen.split(',').map(m => m.trim()) : [];
    
    return MENU_ITEMS.map(item => {
      const hasAccess = modules.includes('all') || modules.includes(item.id);
      const canRead = hasAccess && (rights.includes('view') || rights.includes('read') || 
                    rights.includes('add') || rights.includes('edit') || rights.includes('delete') || 
                    rights.includes('write'));
      const canWrite = hasAccess && (rights.includes('add') || rights.includes('edit') || 
                     rights.includes('delete') || rights.includes('write'));
      
      return {
        menu_id: item.id,
        menu_name: item.name,
        can_read: canRead,
        can_write: canWrite,
        has_access: hasAccess,
      };
    });
  };

  // Check if admin has access to specific menu
  const hasMenuAccess = (menuId: string) => {
    const permissions = getCurrentAdminPermissions();
    const menuPermission = permissions.find(p => p.menu_id === menuId);
    return menuPermission?.has_access || false;
  };

  // Initialize permissions when admin type changes
  useEffect(() => {
    if (formData.admin_type && !editMode) {
      initializePermissions(formData.admin_type);
    }
  }, [formData.admin_type, editMode]);

  const initializePermissions = (adminType: string) => {
    let defaultPermissions: Permission[] = [];

    if (adminType === 'super_admin') {
      // Super admin has full access to everything
      defaultPermissions = MENU_ITEMS.map(item => ({
        menu_id: item.id,
        menu_name: item.name,
        can_read: true,
        can_write: true,
      }));
    } else if (adminType === 'admin') {
      // Admin has full access except user creation (admin_users menu)
      defaultPermissions = MENU_ITEMS.map(item => ({
        menu_id: item.id,
        menu_name: item.name,
        can_read: item.id !== 'admin_users',
        can_write: item.id !== 'admin_users' && item.id !== 'role_master' && item.id !== 'role_mapping',
      }));
    } else if (adminType === 'operation') {
      // Operation has limited access
      defaultPermissions = MENU_ITEMS.map(item => {
        if (item.id === 'order_management') {
          return {
            menu_id: item.id,
            menu_name: item.name,
            can_read: true,
            can_write: true,
          };
        } else if (item.id === 'product_management') {
          return {
            menu_id: item.id,
            menu_name: item.name,
            can_read: true,
            can_write: false,
          };
        } else if (item.id === 'dashboard') {
          return {
            menu_id: item.id,
            menu_name: item.name,
            can_read: true,
            can_write: false,
          };
        } else {
          return {
            menu_id: item.id,
            menu_name: item.name,
            can_read: false,
            can_write: false,
          };
        }
      });
    }

    setFormData(prev => ({ ...prev, permissions: defaultPermissions }));
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleOpenDialog = (mapping?: RoleMapping) => {
    if (mapping) {
      setEditMode(true);
      setSelectedMapping(mapping);
      
      // Parse existing data
      const rights = mapping.rights ? mapping.rights.split(',').map(r => r.trim()) : [];
      const modules = mapping.module_or_screen ? mapping.module_or_screen.split(',').map(m => m.trim()) : [];
      
      // Reconstruct permissions from rights and modules
      const permissions: Permission[] = MENU_ITEMS.map(item => {
        const hasAccess = modules.includes('all') || modules.includes(item.id);
        const canRead = hasAccess && (rights.includes('view') || rights.includes('read'));
        const canWrite = hasAccess && (rights.includes('add') || rights.includes('edit') || rights.includes('delete') || rights.includes('write'));
        
        return {
          menu_id: item.id,
          menu_name: item.name,
          can_read: canRead,
          can_write: canWrite,
        };
      });
      
      setFormData({
        role_id: mapping.role_id.toString(),
        admin_type: mapping.admin_type || 'operation',
        module_or_screen: mapping.module_or_screen,
        rights: mapping.rights,
        permissions: permissions,
      });
    } else {
      setEditMode(false);
      setSelectedMapping(null);
      setFormData({
        role_id: '',
        admin_type: '',
        module_or_screen: '',
        rights: '',
        permissions: [],
      });
    }
    setOpenDialog(true);
    setFormErrors({});
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setSelectedMapping(null);
    setFormData({
      role_id: '',
      admin_type: '',
      module_or_screen: '',
      rights: '',
      permissions: [],
    });
    setFormErrors({});
  };

  const handleRoleChange = (e: SelectChangeEvent) => {
    setFormData(prev => ({ ...prev, role_id: e.target.value }));
    if (formErrors.role_id) {
      setFormErrors(prev => ({ ...prev, role_id: '' }));
    }
  };

  const handleAdminTypeChange = (e: SelectChangeEvent) => {
    const newType = e.target.value as 'super_admin' | 'admin' | 'operation';
    setFormData(prev => ({ ...prev, admin_type: newType }));
    if (formErrors.admin_type) {
      setFormErrors(prev => ({ ...prev, admin_type: '' }));
    }
  };

  const handlePermissionChange = (menuId: string, field: 'can_read' | 'can_write', value: boolean) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.map(perm =>
        perm.menu_id === menuId
          ? { ...perm, [field]: value, ...(field === 'can_read' && !value ? { can_write: false } : {}) }
          : perm
      ),
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.role_id) {
      newErrors.role_id = 'Role is required';
    }

    if (!formData.admin_type) {
      newErrors.admin_type = 'Admin type is required';
    }

    if (formData.permissions.length === 0) {
      newErrors.permissions = 'At least one permission must be configured';
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Convert permissions array to API format
  const convertPermissionsToApiFormat = () => {
    const enabledPermissions = formData.permissions.filter(p => p.can_read || p.can_write);
    
    // Determine module_or_screen based on enabled permissions
    let moduleOrScreen = '';
    if (formData.admin_type === 'super_admin') {
      moduleOrScreen = 'all';
    } else {
      const enabledMenus = enabledPermissions
        .filter(p => p.can_read) // Only include menus with read access
        .map(p => p.menu_id)
        .join(',');
      moduleOrScreen = enabledMenus;
    }

    // Build rights string
    const rightsSet = new Set<string>();
    enabledPermissions.forEach(perm => {
      if (perm.can_read) rightsSet.add('view');
      if (perm.can_write) {
        rightsSet.add('add');
        rightsSet.add('edit');
        rightsSet.add('delete');
      }
    });

    const rightsArray = Array.from(rightsSet);
    if (rightsArray.length === 0) {
      rightsArray.push('view'); // At least view access
    }

    return {
      module_or_screen: moduleOrScreen,
      rights: rightsArray.join(','),
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const apiFormat = convertPermissionsToApiFormat();
      
      if (editMode && selectedMapping) {
        const result = await updateMapping({
          recid: selectedMapping.id,
          role_id: parseInt(formData.role_id),
          admin_type: formData.admin_type,
          module_or_screen: apiFormat.module_or_screen,
          rights: apiFormat.rights,
        }).unwrap();

        if (!result.error) {
          setSnackbar({
            open: true,
            message: result.message || 'Role mapping updated successfully',
            severity: 'success',
          });
          setTimeout(() => {
            handleCloseDialog();
            refetch();
          }, 1000);
        } else {
          setSnackbar({
            open: true,
            message: result.message || 'Failed to update role mapping',
            severity: 'error',
          });
        }
      } else {
        const result = await createMapping({
          role_id: parseInt(formData.role_id),
          admin_type: formData.admin_type,
          module_or_screen: apiFormat.module_or_screen,
          rights: apiFormat.rights,
        }).unwrap();

        if (!result.error) {
          setSnackbar({
            open: true,
            message: result.message || 'Role mapping created successfully',
            severity: 'success',
          });
          setTimeout(() => {
            handleCloseDialog();
            refetch();
          }, 1000);
        } else {
          setSnackbar({
            open: true,
            message: result.message || 'Failed to create role mapping',
            severity: 'error',
          });
        }
      }
    } catch (error: any) {
      console.error('Role mapping operation error:', error);
      setSnackbar({
        open: true,
        message: error?.data?.message || (editMode ? 'Failed to update mapping' : 'Failed to create mapping'),
        severity: 'error',
      });
    }
  };

  

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this role mapping?')) {
      try {
        const result = await deleteMapping(id).unwrap();
        if (!result.error) {
          setSnackbar({
            open: true,
            message: result.message || 'Role mapping deleted successfully',
            severity: 'success',
          });
          setTimeout(() => {
            refetch();
          }, 1000);
        } else {
          setSnackbar({
            open: true,
            message: result.message || 'Failed to delete role mapping',
            severity: 'error',
          });
        }
      } catch (error: any) {
        console.error('Delete mapping error:', error);
        setSnackbar({
          open: true,
          message: error?.data?.message || 'Failed to delete role mapping',
          severity: 'error',
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return (
        date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        }) +
        ', ' +
        date.toLocaleTimeString('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        })
      );
    } catch (error) {
      console.error('Date parsing error:', error);
      return dateString;
    }
  };

  // Get date field based on available data
  const getDateField = (mapping: RoleMapping) => {
    if (mapping.createddt) return mapping.createddt;
    if (mapping.created_at) return mapping.created_at;
    return null;
  };

  const getUpdatedDateField = (mapping: RoleMapping) => {
    if (mapping.updateddt) return mapping.updateddt;
    if (mapping.updated_at) return mapping.updated_at;
    return null;
  };

  // Show menu access preview
  const MenuAccessPreview = () => {
    if (!formData.admin_type || formData.permissions.length === 0) return null;

    const accessibleMenus = formData.permissions.filter(p => p.can_read).map(p => p.menu_name);
    const writableMenus = formData.permissions.filter(p => p.can_write).map(p => p.menu_name);


    const savePermissionsToLocalStorage = (mappingData: any) => {
  try {
    // Get all current mappings
    const currentMappings = mappingsData?.role_mappings || [];
    
    // Add/update the new mapping
    const updatedMappings = editMode && selectedMapping
      ? currentMappings.map(m => m.id === selectedMapping.id ? mappingData : m)
      : [...currentMappings, mappingData];
    
    // Save to localStorage
    localStorage.setItem('role_mappings', JSON.stringify(updatedMappings));
    
    // Also save permissions for the specific admin type
    const permissions = formData.permissions.map(perm => ({
      ...perm,
      has_access: perm.can_read || perm.can_write,
    }));
    
    localStorage.setItem(`permissions_${formData.admin_type}`, JSON.stringify(permissions));
    
    console.log('Permissions saved for:', formData.admin_type);
  } catch (error) {
    console.error('Error saving permissions to localStorage:', error);
  }
};

// Then update the handleSubmit function to call this after successful API call:
const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  if (!validateForm()) return;

  try {
    const apiFormat = convertPermissionsToApiFormat();
    
    // Prepare mapping data
    const mappingData = {
      id: editMode && selectedMapping ? selectedMapping.id : Date.now(),
      role_id: parseInt(formData.role_id),
      role_name: rolesData?.roles?.find(r => r.id === parseInt(formData.role_id))?.role_name || '',
      admin_type: formData.admin_type,
      module_or_screen: apiFormat.module_or_screen,
      rights: apiFormat.rights,
    };
    
    if (editMode && selectedMapping) {
      const result = await updateMapping({
        recid: selectedMapping.id,
        role_id: parseInt(formData.role_id),
        admin_type: formData.admin_type,
        module_or_screen: apiFormat.module_or_screen,
        rights: apiFormat.rights,
      }).unwrap();

      if (!result.error) {
        // Save permissions to localStorage
        savePermissionsToLocalStorage(mappingData);
        
        setSnackbar({
          open: true,
          message: result.message || 'Role mapping updated successfully',
          severity: 'success',
        });
        setTimeout(() => {
          handleCloseDialog();
          refetch();
        }, 1000);
      } else {
        setSnackbar({
          open: true,
          message: result.message || 'Failed to update role mapping',
          severity: 'error',
        });
      }
    } else {
      const result = await createMapping({
        role_id: parseInt(formData.role_id),
        admin_type: formData.admin_type,
        module_or_screen: apiFormat.module_or_screen,
        rights: apiFormat.rights,
      }).unwrap();

      if (!result.error) {
        // Save permissions to localStorage
        savePermissionsToLocalStorage(mappingData);
        
        setSnackbar({
          open: true,
          message: result.message || 'Role mapping created successfully',
          severity: 'success',
        });
        setTimeout(() => {
          handleCloseDialog();
          refetch();
        }, 1000);
      } else {
        setSnackbar({
          open: true,
          message: result.message || 'Failed to create role mapping',
          severity: 'error',
        });
      }
    }
  } catch (error: any) {
    console.error('Role mapping operation error:', error);
    setSnackbar({
      open: true,
      message: error?.data?.message || (editMode ? 'Failed to update mapping' : 'Failed to create mapping'),
      severity: 'error',
    });
  }
};

    return (
      <Box sx={{ mt: 2, p: 2, bgcolor: '#f9fafb', borderRadius: '8px', border: '1px solid #e1e3e5' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#202223' }}>
          Menu Access Preview for {formData.admin_type?.replace('_', ' ').toUpperCase()}
        </Typography>
        <Typography variant="body2" sx={{ color: '#6d7175', mb: 1 }}>
          <strong>Read Access:</strong> {accessibleMenus.length > 0 ? accessibleMenus.join(', ') : 'None'}
        </Typography>
        <Typography variant="body2" sx={{ color: '#6d7175' }}>
          <strong>Write Access:</strong> {writableMenus.length > 0 ? writableMenus.join(', ') : 'None'}
        </Typography>
      </Box>
    );
  };

  // If user doesn't have permission (only super admin can access), show access denied
  if (!hasPermission()) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
        <Box sx={{ display: 'flex', flex: 1 }}>
          {/* Desktop Sidebar */}
          <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
            <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
          </Box>
          
          {/* Main Content */}
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <AdminHeader onToggleMenu={handleDrawerToggle} />
            
            <Box sx={{ 
              flexGrow: 1, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              p: 3 
            }}>
              <Alert severity="error" sx={{ maxWidth: 500 }}>
                <Typography variant="h6" gutterBottom>
                  ⚠️ Access Denied
                </Typography>
                <Typography>
                  You do not have permission to access Role Mapping Management.
                  <br />
                  <strong>Only Super Admin</strong> can manage role mappings.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, p: 1, bgcolor: '#fef2f2', borderRadius: 1 }}>
                  <strong>Your role:</strong> {currentAdminRole || 'Unknown'}
                  <br />
                  <strong>Required role:</strong> Super Admin
                </Typography>
                <Button 
                  variant="outlined" 
                  sx={{ mt: 2 }}
                  onClick={() => window.history.back()}
                >
                  Go Back
                </Button>
              </Alert>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Desktop Sidebar */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />

          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: '#f1f2f4',
            }}
          >
            <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <AssignmentIndIcon sx={{ fontSize: 28, color: '#008060' }} />
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    Role Mapping Management
                  </Typography>
                  <Chip 
                    label="Super Admin Only" 
                    size="small" 
                    color="warning" 
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                  sx={{
                    textTransform: 'none',
                    bgcolor: '#008060',
                    fontWeight: 600,
                    borderRadius: '8px',
                    px: 2.5,
                    '&:hover': { bgcolor: '#006e52' },
                  }}
                >
                  Create Mapping
                </Button>
              </Box>

              {/* Info Alert */}
              <Alert severity="info" sx={{ mb: 2, borderRadius: '8px' }}>
                <Typography variant="body2">
                  <strong>Role Mapping Controls:</strong> Configure what each admin type can access in the sidebar menu.
                  <br />
                  • <strong>Super Admin</strong>: Full access to everything
                  <br />
                  • <strong>Admin</strong>: Full access except user creation & role management
                  <br />
                  • <strong>Operation</strong>: Limited access to orders and products only
                </Typography>
              </Alert>

              {/* Debug Info */}
              {mappingsError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Error loading role mappings: {(mappingsError as any)?.data?.message || 'Unknown error'}
                </Alert>
              )}

              {/* Mappings Table */}
              <Card
                sx={{
                  borderRadius: '16px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  border: '1px solid #e1e3e5',
                  overflow: 'hidden',
                }}
              >
                {isLoadingMappings ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: 300,
                      bgcolor: 'white',
                    }}
                  >
                    <CircularProgress size={32} sx={{ color: '#008060' }} />
                    <Typography sx={{ ml: 2 }}>Loading role mappings...</Typography>
                  </Box>
                ) : mappingsData?.role_mappings && mappingsData.role_mappings.length > 0 ? (
                  <Box sx={{ bgcolor: 'white', overflowX: 'auto' }}>
                    {/* Table Header */}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '80px 1fr 120px 1fr 200px 200px 120px',
                        gap: 2,
                        px: 3,
                        py: 1.5,
                        borderBottom: '1px solid #e1e3e5',
                        bgcolor: '#fafbfb',
                        minWidth: '1200px',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6d7175',
                          fontWeight: 600,
                          fontSize: '11px',
                          textTransform: 'uppercase',
                        }}
                      >
                        S.No
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6d7175',
                          fontWeight: 600,
                          fontSize: '11px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Role Name
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6d7175',
                          fontWeight: 600,
                          fontSize: '11px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Admin Type
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6d7175',
                          fontWeight: 600,
                          fontSize: '11px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Module/Screen Access
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6d7175',
                          fontWeight: 600,
                          fontSize: '11px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Created Date
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6d7175',
                          fontWeight: 600,
                          fontSize: '11px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Updated Date
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6d7175',
                          fontWeight: 600,
                          fontSize: '11px',
                          textTransform: 'uppercase',
                          textAlign: 'right',
                        }}
                      >
                        Actions
                      </Typography>
                    </Box>

                    {/* Table Rows */}
                    {mappingsData.role_mappings.map((mapping, index) => (
                      <Box
                        key={mapping.id}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '80px 1fr 120px 1fr 200px 200px 120px',
                          gap: 2,
                          px: 3,
                          py: 2,
                          alignItems: 'center',
                          borderBottom: '1px solid #e1e3e5',
                          '&:hover': { bgcolor: '#f9fafb' },
                          '&:last-child': { borderBottom: 'none' },
                          transition: 'background-color 0.15s ease',
                          minWidth: '1200px',
                        }}
                      >
                        <Typography variant="body2" sx={{ color: '#6d7175', fontWeight: 500 }}>
                          {index + 1}
                        </Typography>

                        <Box>
                          <Chip
                            label={mapping.role_name || `Role ${mapping.role_id}`}
                            size="small"
                            sx={{
                              bgcolor: mapping.admin_type === 'super_admin' ? '#fff3cd' : 
                                      mapping.admin_type === 'admin' ? '#d1ecf1' : '#d4edda',
                              color: mapping.admin_type === 'super_admin' ? '#856404' : 
                                    mapping.admin_type === 'admin' ? '#0c5460' : '#155724',
                              fontWeight: 600,
                              fontSize: '12px',
                            }}
                          />
                        </Box>

                        <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>
                          {mapping.admin_type || 'N/A'}
                        </Typography>

                        <Box>
                          <Tooltip title={mapping.module_or_screen || 'No modules assigned'}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontSize: '12px', 
                                color: '#6d7175',
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {mapping.module_or_screen === 'all' ? 
                                'All Modules (Full Access)' : 
                                mapping.module_or_screen || 'N/A'}
                            </Typography>
                          </Tooltip>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                          {formatDate(getDateField(mapping))}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                          {formatDate(getUpdatedDateField(mapping))}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(mapping)}
                            sx={{ color: '#6d7175', '&:hover': { bgcolor: '#f1f2f4' } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(mapping.id)}
                            disabled={isDeleting}
                            sx={{ color: '#d32f2f', '&:hover': { bgcolor: '#ffebee' } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                    <AssignmentIndIcon sx={{ fontSize: 64, color: '#c4cdd5', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                      No role mappings found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {mappingsData?.message || 'Click "Create Mapping" to configure role permissions'}
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenDialog()}
                      sx={{
                        textTransform: 'none',
                        bgcolor: '#008060',
                        fontWeight: 600,
                        borderRadius: '8px',
                        px: 2.5,
                        '&:hover': { bgcolor: '#006e52' },
                      }}
                    >
                      Create First Mapping
                    </Button>
                  </Box>
                )}
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: isMobile ? 0 : '16px', maxHeight: '90vh' },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #e1e3e5',
            fontWeight: 600,
            color: '#202223',
          }}
        >
          {editMode ? 'Edit Role Mapping' : 'Create Role Mapping'}
          <IconButton size="small" onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <Stack spacing={3}>
              {/* Role Selection */}
              <FormControl fullWidth error={!!formErrors.role_id} required>
                <InputLabel>Select Role</InputLabel>
                <Select
                  value={formData.role_id}
                  onChange={handleRoleChange}
                  label="Select Role"
                  disabled={isCreating || isUpdating || isLoadingRoles}
                >
                  {isLoadingRoles ? (
                    <MenuItem disabled>Loading roles...</MenuItem>
                  ) : rolesData?.roles && rolesData.roles.length > 0 ? (
                    rolesData.roles.map((role) => (
                      <MenuItem key={role.id} value={role.id.toString()}>
                        {role.role_name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No roles available</MenuItem>
                  )}
                </Select>
                {formErrors.role_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {formErrors.role_id}
                  </Typography>
                )}
              </FormControl>

              {/* Admin Type Selection */}
              <FormControl fullWidth error={!!formErrors.admin_type} required>
                <InputLabel>Admin Type</InputLabel>
                <Select
                  value={formData.admin_type}
                  onChange={handleAdminTypeChange}
                  label="Admin Type"
                  disabled={isCreating || isUpdating}
                >
                  <MenuItem value="super_admin">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LockOpenIcon fontSize="small" />
                      Super Admin (Full Access to All Menus)
                    </Box>
                  </MenuItem>
                  <MenuItem value="admin">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LockIcon fontSize="small" />
                      Admin (All except User Creation & Role Management)
                    </Box>
                  </MenuItem>
                  <MenuItem value="operation">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LockIcon fontSize="small" />
                      Operation (Orders + Read-only Products + Dashboard)
                    </Box>
                  </MenuItem>
                </Select>
                {formErrors.admin_type && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {formErrors.admin_type}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Note: Admin type determines the default permissions template
                </Typography>
              </FormControl>

              {/* Menu Access Preview */}
              <MenuAccessPreview />

              {/* Permissions Table */}
              {formData.permissions.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#202223' }}>
                    Menu Permissions Configuration
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                    • Check <strong>Read Access</strong> to allow viewing the menu
                    <br />
                    • Check <strong>Write Access</strong> to allow modifications (requires Read Access)
                    <br />
                    • Uncheck both to <strong>hide the menu completely</strong> from sidebar
                  </Typography>
                  <TableContainer
                    component={Paper}
                    sx={{
                      maxHeight: 400,
                      border: '1px solid #e1e3e5',
                      borderRadius: '8px',
                      overflow: 'auto',
                    }}
                  >
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ bgcolor: '#fafbfb', fontWeight: 600, fontSize: '12px', width: '40%' }}>
                            Menu Name
                          </TableCell>
                          <TableCell align="center" sx={{ bgcolor: '#fafbfb', fontWeight: 600, fontSize: '12px', width: '30%' }}>
                            Read Access
                            <br />
                            <Typography variant="caption" color="text.secondary">
                              (View in Sidebar)
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ bgcolor: '#fafbfb', fontWeight: 600, fontSize: '12px', width: '30%' }}>
                            Write Access
                            <br />
                            <Typography variant="caption" color="text.secondary">
                              (Create/Edit/Delete)
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {formData.permissions.map((perm) => (
                          <TableRow
                            key={perm.menu_id}
                            sx={{
                              '&:hover': { bgcolor: '#f9fafb' },
                              '&:last-child td': { border: 0 },
                              ...(perm.menu_id === 'admin_users' || perm.menu_id === 'role_master' || perm.menu_id === 'role_mapping') && {
                                bgcolor: '#fff3cd',
                              },
                            }}
                          >
                            <TableCell sx={{ fontSize: '13px' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {perm.menu_name}
                                {(perm.menu_id === 'admin_users' || perm.menu_id === 'role_master' || perm.menu_id === 'role_mapping') && (
                                  <Chip 
                                    label="Restricted" 
                                    size="small" 
                                    color="warning" 
                                    sx={{ height: '20px', fontSize: '10px' }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={perm.can_read}
                                onChange={(e) =>
                                  handlePermissionChange(perm.menu_id, 'can_read', e.target.checked)
                                }
                                disabled={isCreating || isUpdating}
                                sx={{
                                  color: '#008060',
                                  '&.Mui-checked': { color: '#008060' },
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={perm.can_write}
                                onChange={(e) =>
                                  handlePermissionChange(perm.menu_id, 'can_write', e.target.checked)
                                }
                                disabled={!perm.can_read || isCreating || isUpdating}
                                sx={{
                                  color: '#008060',
                                  '&.Mui-checked': { color: '#008060' },
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {formErrors.permissions && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                      {formErrors.permissions}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    <strong>Note:</strong> 
                    <br />• Yellow highlighted rows (Admin Users, Role Master, Role Mapping) are restricted - only Super Admin gets write access
                    <br />• Operation users can only access Order Management, Product Management (read-only), and Dashboard
                  </Typography>
                </Box>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, pt: 2, borderTop: '1px solid #e1e3e5' }}>
            <Button
              onClick={handleCloseDialog}
              disabled={isCreating || isUpdating}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: '#202223',
                borderRadius: '8px',
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isCreating || isUpdating}
              sx={{
                textTransform: 'none',
                bgcolor: '#008060',
                fontWeight: 600,
                borderRadius: '8px',
                px: 2.5,
                '&:hover': { bgcolor: '#006e52' },
              }}
            >
              {isCreating || isUpdating ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} color="inherit" />
                  {editMode ? 'Updating...' : 'Creating...'}
                </Box>
              ) : editMode ? (
                'Update Mapping'
              ) : (
                'Create Mapping'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RoleMappingManagement;